import { createClient } from '@supabase/supabase-js'

// Get environment variables - these should be set in production
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://your-project.supabase.co'
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'your-anon-key'

// Create Supabase client
// detectSessionInUrl is DISABLED — AuthCallback manually parses the hash
// to avoid race conditions with AuthProvider's onAuthStateChange listener.
//
// lock is a NO-OP — @supabase/auth-js 2.70 defaults to navigator.locks
// (Web Locks API) which serialises every auth call behind a single lock.
// If _initialize() is slow (e.g. refreshing a stale token over a flaky
// connection), ALL other calls (setSession, getSession, signOut) queue
// behind it indefinitely.  A no-op lock keeps the API responsive.
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    detectSessionInUrl: false,   // We handle hash parsing manually in AuthCallback
    persistSession: true,         // Persist session to localStorage
    autoRefreshToken: true,       // Auto-refresh expired tokens
    flowType: 'implicit',         // Use implicit flow (hash fragment)
    lock: async (_name, _acquireTimeout, fn) => await fn(),  // Skip navigator.locks
  }
})

// Direct REST fetch for profiles — bypasses the SDK's internal _acquireLock
// queue which deadlocks when setSession() is still running.  The profiles
// table has a public SELECT RLS policy so only the anon key is needed.
// Supports lookup by userId (UUID) or username (string).
export async function fetchProfileDirect(userId, username) {
  const filter = userId
    ? `id=eq.${userId}`
    : `username=eq.${encodeURIComponent(username)}`
  const url = `${supabaseUrl}/rest/v1/profiles?${filter}&select=*`
  const res = await fetch(url, {
    headers: { apikey: supabaseAnonKey, Accept: 'application/json' },
  })
  if (!res.ok) return null
  const rows = await res.json()
  return rows.length > 0 ? rows[0] : null
}

const apiBaseUrl = process.env.REACT_APP_API_URL || window.location.origin

const getAccessToken = async () => {
  try {
    const { data, error } = await supabase.auth.getSession()
    if (error) {
      console.error('Error retrieving Supabase session:', error)
      return null
    }

    return data?.session?.access_token || null
  } catch (error) {
    console.error('Unexpected error retrieving Supabase session:', error)
    return null
  }
}

const buildAuthHeaders = async (additionalHeaders = {}) => {
  const token = await getAccessToken()
  if (token) {
    return {
      ...additionalHeaders,
      Authorization: `Bearer ${token}`,
    }
  }
  return additionalHeaders
}

// Auth API functions
export const authAPI = {
  signUp: async (email, password, userData) => {
    return await supabase.auth.signUp({
      email,
      password,
      options: {
        data: userData
      }
    })
  },

  signIn: async (email, password) => {
    try {
      const result = await supabase.auth.signInWithPassword({
        email,
        password
      })
      return result
    } catch (error) {
      throw error
    }
  },

  signInWithGoogle: async () => {
    try {
      const redirectTo = `${window.location.origin}/auth/callback`
      
      const result = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectTo
        }
      })
      
      return result
    } catch (error) {
      throw error
    }
  },

  signOut: async () => {
    return await supabase.auth.signOut()
  },

  resetPassword: async (email) => {
    return await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/reset-password`
    })
  },

  updatePassword: async (password) => {
    return await supabase.auth.updateUser({ password })
  }
}

// Profile API functions
export const profileAPI = {
  getProfileById: async (userId) => {
    return await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()
  },

  getProfile: async (username) => {
    return await supabase
      .from('profiles')
      .select('*')
      .eq('username', username)
      .single()
  },

  updateProfile: async (userId, updates) => {
    return await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single()
  },

  createProfile: async (profile) => {
    return await supabase
      .from('profiles')
      .insert(profile)
      .select()
      .single()
  }
}

// Collection API functions
export const collectionAPI = {
  // Get user's collections
  getUserCollections: async (userId) => {
    try {
      const headers = await buildAuthHeaders({
        'Content-Type': 'application/json'
      })

      const response = await fetch(`${apiBaseUrl}/api/collections/collections`, {
        method: 'GET',
        headers
      })
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching user collections:', error);
      return { data: [], error };
    }
  },

  // Get another user's public collections + wishlist (no auth needed)
  getPublicProfile: async (userId) => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/collections/public/${userId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      })
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching public profile:', error);
      return { data: null, error };
    }
  },

  // Create a new collection
  createCollection: async (userId, collectionData) => {
    try {
      const headers = await buildAuthHeaders({
        'Content-Type': 'application/json'
      })

      const response = await fetch(`${apiBaseUrl}/api/collections/collections`, {
        method: 'POST',
        headers,
        body: JSON.stringify(collectionData)
      })
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error creating collection:', error);
      return { error };
    }
  },

  // Get collection items
  getCollectionItems: async (collectionId) => {
    try {
      const headers = await buildAuthHeaders({
        'Content-Type': 'application/json'
      })

      const response = await fetch(`${apiBaseUrl}/api/collections/collections/${collectionId}`, {
        method: 'GET',
        headers
      })
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching collection items:', error);
      return { data: null, error };
    }
  },

  // Add item to collection
  addToCollection: async (collectionId, cardId, itemData = {}) => {
    try {
      const headers = await buildAuthHeaders({
        'Content-Type': 'application/json'
      })

      const response = await fetch(`${apiBaseUrl}/api/collections/collections/${collectionId}/items`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          card_id: cardId,
          quantity: itemData.quantity || 1,
          condition: itemData.condition || 'Near Mint',
          purchase_price: itemData.purchase_price,
          purchase_date: itemData.purchase_date,
          notes: itemData.notes
        })
      });
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error adding to collection:', error);
      return { error };
    }
  },

  // Quick add to default collection
  quickAddToCollection: async (cardId, itemData = {}) => {
    try {
      const params = new URLSearchParams({
        card_id: cardId,
        quantity: itemData.quantity || 1,
        condition: itemData.condition || 'Near Mint'
      });

      if (itemData.purchase_price) {
        params.append('purchase_price', itemData.purchase_price);
      }

      const headers = await buildAuthHeaders({
        'Content-Type': 'application/json'
      })

      const response = await fetch(`${apiBaseUrl}/api/collections/collections/quick-add?${params}`, {
        method: 'POST',
        headers
      })
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error quick adding to collection:', error);
      return { error };
    }
  },

  // Remove from collection
  removeFromCollection: async (collectionId, cardId) => {
    try {
      const headers = await buildAuthHeaders({
        'Content-Type': 'application/json'
      })

      const response = await fetch(`${apiBaseUrl}/api/collections/collections/${collectionId}/items/${cardId}`, {
        method: 'DELETE',
        headers
      })
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error removing from collection:', error);
      return { error };
    }
  },

  // Update collection metadata (name, description, is_public)
  updateCollection: async (collectionId, collectionData) => {
    try {
      const headers = await buildAuthHeaders({
        'Content-Type': 'application/json'
      })

      const response = await fetch(`${apiBaseUrl}/api/collections/collections/${collectionId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(collectionData)
      })
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error updating collection:', error);
      return { error };
    }
  },

  // Delete collection
  deleteCollection: async (collectionId) => {
    try {
      const headers = await buildAuthHeaders({
        'Content-Type': 'application/json'
      })

      const response = await fetch(`${apiBaseUrl}/api/collections/collections/${collectionId}`, {
        method: 'DELETE',
        headers
      })
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error deleting collection:', error);
      return { error };
    }
  },

  // Update collection item
  updateCollectionItem: async (collectionId, cardId, itemData) => {
    try {
      const headers = await buildAuthHeaders({
        'Content-Type': 'application/json'
      })

      const response = await fetch(`${apiBaseUrl}/api/collections/collections/${collectionId}/items/${cardId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(itemData)
      })
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error updating collection item:', error);
      return { error };
    }
  }
}

// Wishlist API functions
export const wishlistAPI = {
  // Get user's wishlist
  getUserWishlist: async (userId) => {
    try {
      const headers = await buildAuthHeaders({
        'Content-Type': 'application/json'
      })

      const response = await fetch(`${apiBaseUrl}/api/collections/wishlist`, {
        method: 'GET',
        headers
      })
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching user wishlist:', error);
      return { data: [], error };
    }
  },

  // Add to wishlist
  addToWishlist: async (cardId, itemData = {}) => {
    try {
      const headers = await buildAuthHeaders({
        'Content-Type': 'application/json'
      })

      const response = await fetch(`${apiBaseUrl}/api/collections/wishlist`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          card_id: cardId,
          target_price: itemData.target_price || null,
          notes: itemData.notes
        })
      });
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      return { error };
    }
  },

  // Quick add to wishlist
  quickAddToWishlist: async (cardId, targetPrice = null) => {
    try {
      const params = new URLSearchParams({
        card_id: cardId,
      });
      if (targetPrice != null) {
        params.set('target_price', targetPrice)
      }

      const headers = await buildAuthHeaders({
        'Content-Type': 'application/json'
      })

      const response = await fetch(`${apiBaseUrl}/api/collections/wishlist/quick-add?${params}`, {
        method: 'POST',
        headers
      })
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error quick adding to wishlist:', error);
      return { error };
    }
  },

  // Remove from wishlist
  removeFromWishlist: async (cardId) => {
    try {
      const headers = await buildAuthHeaders({
        'Content-Type': 'application/json'
      })

      const response = await fetch(`${apiBaseUrl}/api/collections/wishlist/${cardId}`, {
        method: 'DELETE',
        headers
      })
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      return { error };
    }
  },

  // Update wishlist item
  updateWishlistItem: async (cardId, itemData) => {
    try {
      const headers = await buildAuthHeaders({
        'Content-Type': 'application/json'
      })

      const response = await fetch(`${apiBaseUrl}/api/collections/wishlist/${cardId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(itemData)
      })
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error updating wishlist item:', error);
      return { error };
    }
  }
}

export default supabase
